/**
 * simulateur.jsx - Simulateur simple de budget long terme
 * 
 * Version basique avec email gate pour débloquer les résultats.
 * 
 * Fonctionnalités :
 * - 4 paramètres : objectif, montant initial, mensualités, rendement annuel
 * - Calcul du délai pour atteindre l'objectif avec intérêts composés
 * - Graphique d'évolution (via ChartResults)
 * - Validation email (EmailJS) pour voir les résultats
 * - CTA vers simulateur premium
 * 
 * Flow :
 * 1. Utilisateur remplit les 4 champs
 * 2. Calcul automatique de la simulation
 * 3. Demande d'email pour débloquer les résultats
 * 4. Envoi via EmailJS + stockage dans localStorage
 * 5. Affichage des résultats + graphique
 * 
 * Formule de calcul :
 * amount(n+1) = amount(n) * (1 + r) + m
 * où :
 *   - r = taux mensuel (rateAnnual / 100 / 12)
 *   - m = mensualité
 */

import { useMemo, useState } from "react";
import "./simulateur.css";
import { Link, useNavigate } from "react-router-dom";
import emailjs from "emailjs-com";
import ChartResults from "../components/chartResults";
import { useSEO } from "../seo/useSEO";

/* ================== Devises ================== */
/**
 * Liste des devises supportées
 * Code ISO 4217 pour les symboles de devises
 */
const CURRENCIES = [
  { code: "EUR", label: "€ Euro" },
  { code: "USD", label: "$ Dollar" },
  { code: "GBP", label: "£ Livre" },
  { code: "XOF", label: "F CFA (XOF)" },
];

/**
 * Formateur de montant monétaire selon la devise
 * @param {string} currency - Code ISO de la devise (EUR, USD, etc.)
 * @returns {Intl.NumberFormat} Formateur configuré
 */
function moneyFormatter(currency) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0, // Pas de décimales (ex: 1000€ au lieu de 1000,00€)
  });
}

/* ================== Utils ================== */

/**
 * Limite une valeur entre un min et un max
 * @param {number} n - Valeur à limiter
 * @param {number} a - Minimum
 * @param {number} b - Maximum
 * @returns {number} Valeur clampée
 */
function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

/**
 * Convertit un nombre de mois en texte lisible
 * @param {number} total - Nombre total de mois
 * @returns {string} Texte formaté (ex: "5 ans et 7 mois")
 * @example
 * monthsToHuman(67) // "5 ans et 7 mois"
 * monthsToHuman(12) // "1 an"
 * monthsToHuman(7) // "7 mois"
 */
function monthsToHuman(total) {
  if (!isFinite(total)) return "∞";
  const y = Math.floor(total / 12);
  const m = total % 12;
  if (y && m) return `${y} an${y > 1 ? "s" : ""} et ${m} mois`;
  if (y) return `${y} an${y > 1 ? "s" : ""}`;
  return `${m} mois`;
}

/**
 * Valide le format d'un email
 * @param {string} email - Email à valider
 * @returns {boolean} True si email valide
 */
function isValidEmail(email) {
  const v = String(email || "").trim();
  // Regex simple : xxx@xxx.xx (minimum 2 caractères après le point)
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
}

/* ================== Simulation ================== */

/**
 * Calcule le nombre de mois pour atteindre un objectif avec intérêts composés
 * 
 * Algorithme :
 * - Chaque mois : amount = amount * (1 + r) + m
 * - r = taux mensuel (rateAnnual / 100 / 12)
 * - m = mensualité
 * - Cap à 1200 mois (100 ans) pour éviter les boucles infinies
 * 
 * @param {Object} params - Paramètres de simulation
 * @param {number} params.goal - Montant objectif à atteindre
 * @param {number} params.start - Montant initial (épargne de départ)
 * @param {number} params.monthly - Mensualité (montant ajouté chaque mois)
 * @param {number} params.rateAnnual - Taux de rendement annuel en % (ex: 2.5)
 * @returns {Object} Résultat de simulation
 * @returns {number} return.months - Nombre de mois (Infinity si impossible)
 * @returns {boolean} return.reached - True si objectif atteignable
 * @returns {number} return.finalAmount - Montant final atteint
 * @returns {number} return.totalPaid - Total des versements effectués
 * @returns {Array<{month: number, amount: number}>} return.points - Points pour le graphique
 * 
 * @example
 * simulate({ goal: 20000, start: 0, monthly: 300, rateAnnual: 0 })
 * // { months: 67, reached: true, finalAmount: 20100, totalPaid: 20100, points: [...] }
 */
function simulate({ goal, start, monthly, rateAnnual }) {
  // Normalisation des valeurs (pas de négatifs)
  const g = Math.max(0, goal);
  let amount = Math.max(0, start);
  const m = Math.max(0, monthly);
  const r = Math.max(0, rateAnnual) / 100 / 12; // Conversion taux annuel → mensuel

  // Cas 1 : Objectif déjà atteint
  if (g <= amount) {
    return {
      months: 0,
      reached: true,
      finalAmount: amount,
      totalPaid: 0,
      points: [{ month: 0, amount }],
    };
  }

  // Cas 2 : Impossible d'atteindre l'objectif (pas de mensualité ni de rendement)
  if (m === 0 && r === 0) {
    return {
      months: Infinity,
      reached: false,
      finalAmount: amount,
      totalPaid: 0,
      points: [{ month: 0, amount }],
    };
  }

  // Cas 3 : Simulation mois par mois avec intérêts composés
  let months = 0;
  let totalPaid = 0;
  const points = [{ month: 0, amount }]; // Point de départ pour le graphique

  // Boucle de simulation (cap à 1200 mois = 100 ans)
  while (amount < g && months < 1200) {
    // Formule : amount(n+1) = amount(n) * (1 + r) + m
    // 1. Appliquer le rendement mensuel : amount * (1 + r)
    // 2. Ajouter la mensualité : + m
    amount = amount * (1 + r) + m;
    totalPaid += m;
    months++;
    points.push({ month: months, amount }); // Ajouter un point pour le graphique
  }

  return {
    months: amount >= g ? months : Infinity,
    reached: amount >= g,
    finalAmount: amount,
    totalPaid,
    points,
  };
}


/* ================== Component ================== */

/**
 * Composant Simulate - Simulateur simple avec email gate
 * 
 * État :
 * - currency : Devise sélectionnée (EUR, USD, etc.)
 * - goal, start, monthly, rateAnnual : Paramètres de simulation
 * - email, emailOk, emailTouched : Gestion du gate email
 * - isSending, sendError : État UX EmailJS
 * 
 * @returns {JSX.Element} Page du simulateur simple
 */
export default function Simulate() {
  const navigate = useNavigate();

  useSEO({
    title: "Simulateur budget — Objectif & mensualités | HowMuch",
    description:
      "Calcule en combien de temps tu atteins ton objectif financier selon ton montant initial, tes mensualités et un rendement optionnel.",
    canonicalPath: "/simulate",
    ogType: "website",
  });

  // Paramètres de simulation
  const [currency, setCurrency] = useState("EUR");
  const [goal, setGoal] = useState("20000");
  const [start, setStart] = useState("0");
  const [monthly, setMonthly] = useState("300");
  const [rateAnnual, setRateAnnual] = useState("0");

  // Email gate (validation pour voir les résultats)
  // Récupérer l'email depuis localStorage si déjà validé
  const [email, setEmail] = useState(() => localStorage.getItem("howmuch_email") || "");
  const [emailOk, setEmailOk] = useState(() => localStorage.getItem("howmuch_email_ok") === "1");
  const [emailTouched, setEmailTouched] = useState(false);

  // État UX pour l'envoi EmailJS
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");

  // Formateur monétaire selon la devise sélectionnée
  const MONEY = useMemo(() => moneyFormatter(currency), [currency]);

  // Calcul automatique de la simulation à chaque changement de paramètres
  const result = useMemo(
    () => simulate({ goal, start, monthly, rateAnnual }),
    [goal, start, monthly, rateAnnual]
  );

  // Calcul du pourcentage de progression actuel
  const progress = clamp((start / Math.max(1, goal)) * 100, 0, 100);
  
  // Débloquer les résultats uniquement si email validé
  const canShowResults = emailOk && isValidEmail(email);

  /**
   * Soumet l'email via EmailJS pour débloquer les résultats
   * Stocke la validation dans localStorage pour persistance
   * 
   * Configuration EmailJS :
   * - Service ID : service_3wu5jp8
   * - Template ID : template_bwvihgr
   * - Public Key : -t1fWH2pBxfysOOtL
   */
  async function onSubmitEmail(e) {
    e.preventDefault();
    setEmailTouched(true);
    setSendError("");

    if (!isValidEmail(email)) return;

    try {
      setIsSending(true);

      // Envoi via EmailJS (authentification avec clés publiques)
      await emailjs.send(
        "service_3wu5jp8",    // Service ID
        "template_bwvihgr",   // Template ID
        {
          user_email: email,  // Variable du template EmailJS
          // Optionnel : ajouter d'autres variables si besoin
          // to_email: "destinataire@domaine.com",
        },
        "-t1fWH2pBxfysOOtL"    // Public Key
      );

      // Stocker la validation dans localStorage pour ne pas redemander
      localStorage.setItem("howmuch_email", email.trim());
      localStorage.setItem("howmuch_email_ok", "1");
      setEmailOk(true); // Débloquer les résultats
    } catch (err) {
      console.error("Email failed", err);
      setSendError("Impossible d’enregistrer l’email. Réessaie dans quelques secondes.");
    } finally {
      setIsSending(false);
    }
  }

  /**
   * Redirige vers le simulateur premium avec les paramètres actuels
   * Passe les données via state de React Router pour pré-remplir le formulaire
   */
  function goPremium() {
    navigate("/simulate/premium", {
      state: {
        currency,
        goal,
        current: start,      // Mapper "start" vers "current" (nomenclature du premium)
        monthly,             // Utile pour affichage ou scénarios
        rateAnnual,
      },
    });
  }

  return (
    <main className="sim">
      <header className="ps-header">
        <div className="ps-container ps-header-inner">
          <Link to="/" className="ps-brand">
            <div className="ps-logo">HM</div>
            <div className="ps-brand-text">
              <div className="ps-brand-name">HowMuch</div>
              <div className="ps-brand-tag">Simulations de budget long terme</div>
            </div>
          </Link>

          <div className="ps-header-actions">
            <Link to="/blog" className="ps-btn ps-btn-ghost">
              Blog
            </Link>

            <select className="ps-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <section className="sim-container sim-grid">
        {/* PARAMÈTRES */}
        <aside className="sim-card">
          <div className="sim-card-head">
            <div className="sim-card-title">Paramètres</div>
          </div>

          <div className="sim-fields">
            <label className="sim-label">
              Objectif
              <input
                className="sim-input"
                type="number"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
              />
            </label>

            <label className="sim-label">
              Montant initial
              <input
                className="sim-input"
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>

            <label className="sim-label">
              Mensualités
              <input
                className="sim-input"
                type="number"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
              />
            </label>

            <label className="sim-label">
              Rendement annuel ({rateAnnual}%)
              <input
                className="sim-input"
                type="number"
                value={rateAnnual}
                onChange={(e) => setRateAnnual(e.target.value)}
              />
            </label>
          </div>
        </aside>

        {/* RÉSULTATS */}
        <div className="sim-right">
          <section className="sim-card sim-card-hero">

              <div className="sim-result">
                <div>
                  <div className="sim-result-label">Délai estimé</div>
                  <div className="sim-result-value">{monthsToHuman(result.months)}</div>
                </div>

                <div className="sim-kpis">
                  <div className="sim-kpi">
                    <span>Objectif</span>
                    <b>{MONEY.format(goal)}</b>
                  </div>
                  <div className="sim-kpi">
                    <span>Versements</span>
                    <b>{isFinite(result.totalPaid) ? MONEY.format(result.totalPaid) : "—"}</b>
                  </div>
                  <div className="sim-kpi">
                    <span>Final</span>
                    <b>{isFinite(result.finalAmount) ? MONEY.format(result.finalAmount) : "—"}</b>
                  </div>
                </div>

                <div className="sim-progress">
                  <div className="sim-progress-top">
                    <span>Déjà épargné</span>
                    <span>{progress.toFixed(0)}%</span>
                  </div>
                  <div className="sim-bar">
                    <div className="sim-bar-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* CTA vers Premium */}
                <div className="sim-upgrade">
                  <div className="sim-upgrade-title">Tu veux optimiser ton plan ?</div>
                  <div className="sim-upgrade-text">
                    Compare plusieurs scénarios et trouve la stratégie la plus rapide.
                  </div>
                  <button className="sim-upgrade-btn" onClick={goPremium}>
                    Voir les simulations avancées
                  </button>
                </div>
              </div>
            
          </section>

          {/* GRAPHE */}
          <section className="sim-card">
            <div className="sim-card-head">
              <div className="sim-card-title">Évolution (courbe)</div>
            </div>

          </section>
        </div>
      </section>
    </main>
  );
}
